import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { fetchWordsByFilter, fetchSingleWordByFilter, sendToMeasurementsWithParams } from "@app/routes/vocabulary/api/index";
import { PREVIEW_BOARD_ID, simpleSetPayload } from "../constants";
import { MEASUREMENT_TYPES } from "@amplio-common/measurements/constants";
import { sortByFieldAlphabetically } from "@amplio-common/funcs/commonFunctionalities";
import { SLICE_NAMES } from "./sliceNames";

// Async actions
export const fetchWords = createAsyncThunk(
  `${SLICE_NAMES.ARTICULATION_SLICE}/fetchWords`,
  async ({ studentId, filters, limit }) => {
    const { response } = await fetchWordsByFilter({ filters, limit });
    // thunkAPI.dispatch(updateStudentStatus({ id: studentId, changes: { studentWords: response, isWordsLoading: false } })); // TODO - use your own slice!
    return { studentWords: response, studentId };
  },
);

export const fetchSingleWord = createAsyncThunk(
  `${SLICE_NAMES.ARTICULATION_SLICE}/fetchSingleWord`,
  async ({ studentId, filters }) => {
    const { response } = await fetchSingleWordByFilter({ filters });
    // thunkAPI.dispatch(updateStudentStatus({ id: studentId, changes: { studentWords: response, isWordsLoading: false } })); // TODO - use your own slice!
    return { studentWords: response, studentId };
  },
);

export const fetchWordUUID = createAsyncThunk(
  `${SLICE_NAMES.ARTICULATION_SLICE}/fetchWordUUID`,
  async ({ word, gameUUID, studentId, isNaming, item }, thunkAPI) => {
    const measurementType = isNaming ? MEASUREMENT_TYPES.NAMING : MEASUREMENT_TYPES.ARTICULATION;
    const { response } = await sendToMeasurementsWithParams({ measurements: [{ word, student_id: studentId }],
      uuid: gameUUID,
      measurement_type: measurementType,
      instant_create: 1 });
    const wordUUID = response.measurements[0].uuid;
    // thunkAPI.dispatch(updateStudentStatus({ id: studentId, changes: { wordUUID } })); // TODO - use your own slice!
    // eslint-disable-next-line no-use-before-define
    thunkAPI.dispatch(sendWordUUIDToStudent({ id: studentId, uuid: wordUUID, item }));
    return { wordUUID, studentId };
  },
);

export const sendResultToMeasurement = createAsyncThunk(
  `${SLICE_NAMES.ARTICULATION_SLICE}/sendResultToMeasurement`,
  async ({ result, uuid, gameUUID, isNaming, cue, approximate }) => {
    const measurementType = isNaming ? MEASUREMENT_TYPES.NAMING : MEASUREMENT_TYPES.ARTICULATION;
    const { response } = await sendToMeasurementsWithParams({
      measurements: [{ score: +result, uuid, cue, approximate }], uuid: gameUUID, measurement_type: measurementType,
    });
    return response;
  },
);

//define root selector
const ARTICULATION_SLICE_STATE_ROOT_SELECTOR = state => state[SLICE_NAMES.ARTICULATION_SLICE];
const sortFunc = sortByFieldAlphabetically("name");

export const articulationStudentsAdapter = createEntityAdapter({
  sortComparer: sortFunc,
});

const initialState = {
  previewWords: [],
  excludedWords: [],
  areWordsLoading: false,
  isSingleWordLoading: false,
  ...articulationStudentsAdapter.getInitialState(),
};

export const articulationMapTSToStudentStatus = (ts) => ({
  id: ts.student.id,
  name: ts.student.name,
  studentWords: [],
  studentExcludedWords: [],
  areWordsLoading: false,
  isSingleWordLoading: false,
  isSingleWordLoadError: false,
  studentSelectedWord: null,
  isShowArticulationDialog: false,
  isStudentRecording: false,
  wordUUID: null,
});

export const articulationSlice = createSlice({
  name: SLICE_NAMES.ARTICULATION_SLICE,
  initialState,
  reducers: {
    clearArticulation: () => ({ ...initialState }),
    sendWordUUIDToStudent: () => {},
    sendWordToStudent: () => { },
    setAreWordsLoading: simpleSetPayload("areWordsLoading"),
    setIsSingleWordLoading: simpleSetPayload("isSingleWordLoading"),
    setIsSingleWordLoadError: simpleSetPayload("isSingleWordLoadError"),
    setPreviewWords: simpleSetPayload("previewWords"),
    articulationUpdateStudentStatus: articulationStudentsAdapter.updateOne,
    articulationSetStudentsStatus: articulationStudentsAdapter.setAll,
    articulationUpdateAllStudents: articulationStudentsAdapter.updateMany,
    setExcludedWords: (state, action) => {
      // state.excludedWords.push(action.payload);
      state.excludedWords = action.payload;
    },
    clearWordsFilter: (state) => ({
      ...state,
      previewWords: [],
      excludedWords: [],
    }),
  },
  extraReducers: {
    [fetchWords.fulfilled]: (state, action) => {
      const { studentId, studentWords } = action.payload;
      if (studentId && studentId.length > 0) {
        studentId.forEach(id => {
          if (id !== PREVIEW_BOARD_ID) {
            state.entities[id].areWordsLoading = false;
            state.entities[id].studentWords = studentWords;
          }
        });
      }
      if (!studentId || studentId?.length > 1) {
        state.previewWords = studentWords;
      }
      // if (state.previewWords.length === 0) { // only once since it's a placeholder
      //   state.previewWords = studentWords;
      // }

      state.areWordsLoading = false;
    },
    [fetchWords.rejected]: (state, action) => {
      const studentId = action?.meta?.arg?.studentId;
      
      if (studentId && studentId.length > 0) {
        studentId.forEach(id => {
          if (id !== PREVIEW_BOARD_ID) {
            state.entities[studentId].areWordsLoading = false;
            state.entities[studentId].studentWords = [];
          }
        });
      }
      if (!studentId || studentId?.length > 1) {
        state.previewWords = [];
      }
      state.areWordsLoading = false;
    },
    [fetchSingleWord.fulfilled]: (state, action) => {
      const { studentId, studentWords } = action.payload;
      if (studentId && studentId.length > 0) {
        studentId.forEach(id => {
          if (id !== PREVIEW_BOARD_ID) {
            state.entities[studentId].areWordsLoading = false;
            state.entities[studentId].studentWords.push(...studentWords);
          }
        });
      }
      if (!studentId || studentId?.length > 1) {
        state.previewWords.push(...studentWords);
      }
      state.isSingleWordLoading = false;
      state.isSingleWordLoadError = false;
    },
    [fetchSingleWord.rejected]: state => {
      state.isSingleWordLoading = false;
      state.isSingleWordLoadError = true;
    },
    [fetchWordUUID.fulfilled]: (state, action) => {
      const { studentId, wordUUID } = action.payload;
      state.entities[studentId].wordUUID = wordUUID;
    },
  },
});

const articulationStudentsAdapterSelectors = articulationStudentsAdapter.getSelectors(ARTICULATION_SLICE_STATE_ROOT_SELECTOR);
export default articulationSlice.reducer;

//actions
export const { sendWordUUIDToStudent, sendWordToStudent, clearArticulation,
  articulationUpdateStudentStatus, articulationSetStudentsStatus, articulationUpdateAllStudents,
  setAreWordsLoading, setPreviewWords, setExcludedWords, setIsSingleWordLoading, setIsSingleWordLoadError, clearWordsFilter } = articulationSlice.actions;

//selectors
export const {
  selectAll: articulationSelectAllStudentsStatus, selectIds: articulationSelectStudentStatusIds, selectById: articulationSelectStudentStatusById,
} = articulationStudentsAdapterSelectors;

const getPreviewWords = state => state.articulationSlice.previewWords;
export const selectPreviewWords = createSelector([getPreviewWords], previewWords => previewWords);

const getAreWordsLoading = state => state.articulationSlice.areWordsLoading;
export const selectAreWordsLoading = createSelector([getAreWordsLoading], areWordsLoading => areWordsLoading);

const getIsSingleWordLoading = state => state.articulationSlice.isSingleWordLoading;
export const selectIsSingleWordLoading = createSelector([getIsSingleWordLoading], isSingleWordLoading => isSingleWordLoading);

const getIsSingleWordLoadError = state => state.articulationSlice.isSingleWordLoadError;
export const selectIsSingleWordLoadError = createSelector([getIsSingleWordLoadError], isSingleWordLoadError => isSingleWordLoadError);

const getExcludedWords = state => state.articulationSlice.excludedWords;
export const selectedExcludedWords = createSelector([getExcludedWords], excludedWords => excludedWords);
